import {
  DeliveryOption,
  DeliveryOptionRecurring,
  compareMinPackSize,
  getSavingsPercent,
  isRecurring,
  isYearly,
} from '@/product/delivery-option';

import ComponentToggleSignal from '@/components/component-toggle-signal';
import DeliverySelectorLabel from '@/experiments/product-select-v3/DeliverySelectorLabel';
import FrequencyHelperSelector from './FrequencyHelperSelector';
import Modal from '@/components/Modal';
import PackSizeSelector from '@/experiments/product-select-v3/PackSizeSelector';
import ProductSelectController from '@/components/buy-stack/ProductSelectController';
import SelectorItemBox from '@/experiments/product-select-v3/SelectorItemBox';
import SellingPlanSelector from '@/experiments/product-select-v3/SellingPlanSelector';
import Settings from '@/components/buy-stack/ProductSelectSettings';
import checkIcon from '@/icons/check-icon.svg';
import classNames from 'classnames';
import {getColorStyle} from '@/utilities/color-style-map';
import {useEffect, useState} from 'preact/hooks';

/**
 * A selector for choosing between delivery options.
 * Options with a selling plan are considered subscriptions.
 * Options with no selling plan are considered one-time purchases.
 */
export default function DeliverySelector({
  currency = 'USD',
  locale,
  color = 'primary-blue',
  deliveryOptions,
  onUpdate,
  value,
}: DeliverySelectorProps) {
  const onetimeOptions = deliveryOptions.filter(
    (option) => !isRecurring(option)
  );
  onetimeOptions.sort(compareMinPackSize);

  const monthlyOptions = deliveryOptions.filter(
    (option) => isRecurring(option) && !isYearly(option)
  ) as DeliveryOptionRecurring[];
  monthlyOptions.sort(
    (a, b) =>
      a.sellingPlan.orderIntervalFrequency -
      b.sellingPlan.orderIntervalFrequency
  );

  const [selected, setSelected] = useState<DeliveryOption | null>(
    value ?? monthlyOptions[0] ?? onetimeOptions[0] ?? null
  );

  const [selectedOnetime, setSelectedOnetime] = useState<DeliveryOption | null>(
    selected && !isRecurring(selected) ? selected : onetimeOptions[0] ?? null
  );

  const [selectedRecurring, setSelectedRecurring] =
    useState<DeliveryOption | null>(
      selected && isRecurring(selected) ? selected : monthlyOptions[0] ?? null
    );

  // update each selected state when the value prop changes or the deliveryOptions change
  useEffect(() => {
    setSelected(value ?? monthlyOptions[0] ?? onetimeOptions[0] ?? null);
    setSelectedOnetime(
      selected && !isRecurring(selected) ? selected : onetimeOptions[0] ?? null
    );
    setSelectedRecurring(
      selected && isRecurring(selected) ? selected : monthlyOptions[0] ?? null
    );
  }, [
    value,
    deliveryOptions,
    onetimeOptions,
    monthlyOptions,
    selected,
    selectedOnetime,
    selectedRecurring,
  ]);

  const subscribeAndSavePercent = selectedRecurring
    ? getSavingsPercent(selectedRecurring)
    : 0;

  const modalController = new ComponentToggleSignal();

  const handleSelectOption = (option: DeliveryOption) => {
    if (modalController.isOpen) {
      modalController.close();
    }

    if (isRecurring(option)) {
      setSelectedRecurring(option);
    } else {
      setSelectedOnetime(option);
    }
    setSelected(option);
    onUpdate && onUpdate(option);
  };

  const save_label = (percent: number, showZero = false) =>
    percent || showZero ? `SAVE ${percent}%` : '';

  const colorStyle = getColorStyle(color);

  const handleHelperClick = () => modalController.open();

  return (
    <ProductSelectController.Consumer>
      {(controller) => {
        const recurringItem = selectedRecurring
          ? controller.getSelectedDeliveryItem(selectedRecurring)
          : null;
        const onetimeItem = selectedOnetime
          ? controller.getSelectedDeliveryItem(selectedOnetime)
          : null;

        return (
          <Settings.Consumer>
            {({
              delivery_selector_title,
              monthly_subscription_selector: {
                label: subscribe_label,
                badge_text: subscribe_badge_text,
              },
              one_time_purchase_selector: {
                label: one_time_purchase_label,
                badge_text: one_time_badge_text,
              },
              subscription_guarantee,
              subscription_terms,
              format_money_with_decimals = true,
              frequency_helper_button_label,
              frequency_helper_options,
            }) => {
              const showFrequencyHelper =
                frequency_helper_options &&
                frequency_helper_options?.length > 0;
              const oneTimeLabel =
                one_time_purchase_label ?? 'One-time Purchase';
              return (
                <>
                  <div className="grid grid-cols-1 gap-y-3">
                    {delivery_selector_title && (
                      <h6 className="text-center text-sm font-semibold uppercase">
                        {delivery_selector_title}:
                      </h6>
                    )}
                    {selectedOnetime && onetimeItem && (
                      <SelectorItemBox
                        dataTestid="one-time-purchase"
                        badgeLabel={one_time_badge_text}
                        color={color}
                        isSelected={!!selected && !isRecurring(selected)}
                        onClick={() => handleSelectOption(selectedOnetime)}
                        label={
                          <DeliverySelectorLabel
                            compareAtPrice={onetimeItem.compareAtPrice}
                            currency={currency}
                            locale={locale}
                            formatMoneyWithDecimals={format_money_with_decimals}
                            price={onetimeItem.price}
                          >
                            {oneTimeLabel}
                          </DeliverySelectorLabel>
                        }
                        showBackgroundColor={true}
                      >
                        {onetimeOptions.length > 1 && (
                          <PackSizeSelector
                            color={color}
                            onUpdate={handleSelectOption}
                            options={onetimeOptions}
                            value={selectedOnetime}
                          />
                        )}
                      </SelectorItemBox>
                    )}
                    {selectedRecurring && recurringItem && (
                      <SelectorItemBox
                        dataTestid="recurring-purchase"
                        badgeLabel={subscribe_badge_text}
                        color={color}
                        label={
                          <DeliverySelectorLabel
                            compareAtPrice={recurringItem.compareAtPrice}
                            currency={currency}
                            locale={locale}
                            formatMoneyWithDecimals={format_money_with_decimals}
                            price={recurringItem.price}
                          >
                            <span>
                              {subscribe_label} &{' '}
                              <span
                                className={classNames(
                                  'font-semibold',
                                  colorStyle.text
                                )}
                              >
                                {save_label(subscribeAndSavePercent)}
                              </span>
                            </span>
                          </DeliverySelectorLabel>
                        }
                        footer={
                          <div className="flex flex-col items-center gap-y-2 pb-3">
                            {showFrequencyHelper && (
                              <p
                                className="cursor-pointer underline"
                                onClick={handleHelperClick}
                              >
                                {frequency_helper_button_label}
                              </p>
                            )}
                            <p className="text-center text-xs font-medium uppercase leading-6">
                              <img
                                src={checkIcon}
                                alt="Check Icon"
                                loading="lazy"
                                height="16"
                                width="16"
                                className="inline-block align-top"
                              />
                              {subscription_guarantee}
                              <br />
                              <img
                                src={checkIcon}
                                alt="Check Icon"
                                loading="lazy"
                                height="16"
                                width="16"
                                className="inline-block align-top"
                              />
                              {subscription_terms}
                            </p>
                          </div>
                        }
                        isSelected={!!selected && isRecurring(selected)}
                        onClick={() => handleSelectOption(selectedRecurring)}
                        showBackgroundColor={true}
                      >
                        <SellingPlanSelector
                          color={color}
                          onUpdate={handleSelectOption}
                          options={monthlyOptions}
                          value={selectedRecurring as DeliveryOptionRecurring}
                        />
                      </SelectorItemBox>
                    )}
                  </div>
                  {showFrequencyHelper && (
                    <Modal controller={modalController}>
                      <div className="flex h-full flex-col">
                        <div style={{flex: 2}}></div>
                        <FrequencyHelperSelector
                          options={monthlyOptions}
                          onUpdate={handleSelectOption}
                          value={selectedRecurring as DeliveryOptionRecurring}
                        />
                        <div style={{flex: 3}}></div>
                      </div>
                    </Modal>
                  )}
                </>
              );
            }}
          </Settings.Consumer>
        );
      }}
    </ProductSelectController.Consumer>
  );
}

export type DeliverySelectorProps = {
  color?: string;
  currency?: string;
  locale?: string;
  deliveryOptions: DeliveryOption[];
  onUpdate?: (deliveryOption: DeliveryOption) => void;
  subscriptionTerms?: string;
  value?: DeliveryOption;
};
